import {
  EWorkflowRunStatus,
  EWorkflowStepRunStatus,
  EWorkflowStepType,
  EWorkflowTriggerType,
} from "../types/workflow";
import {
  HOOK_EVENT_TYPE_OPTIONS,
  NON_SELECTABLE_HOOK_EVENT_TYPE_OPTIONS,
} from "./automation";

// NOTE: NSAI-6936 — react-query keys for the Workflows tab on web and mobile.
// The run hooks append the workflow uuid (runs) or the run uuid (detail).
export const WORKFLOWS_QUERY_KEY = "workflows";
export const WORKFLOW_DETAIL_QUERY_KEY = "workflowDetail";
export const WORKFLOW_RUNS_QUERY_KEY = "workflowRuns";
export const WORKFLOW_RUN_DETAIL_QUERY_KEY = "workflowRunDetail";

// NOTE: Mirrors MAX_WORKFLOW_STEPS in
// apps/nsai/server/src/automation/constants/workflow.constants.ts — the DTO's
// ArrayMaxSize. Keep the two in step.
export const MAX_WORKFLOW_STEPS = 20;

// NOTE: How many runs the history shows. Sent explicitly so the tab does not
// follow a change to the server's default page size.
export const WORKFLOW_RUNS_LIMIT = 20;

// NOTE: While a run is in flight or held at a gate, the history re-polls so a
// finished step or a decision made elsewhere shows up without a manual refresh.
export const WORKFLOW_RUNS_POLL_INTERVAL_MS = 5_000;

export const DEFAULT_WORKFLOW_CRON_EXPRESSION = "0 9 * * 1-5";
export const DEFAULT_WORKFLOW_TIMEOUT_SECONDS = 600;

export const WORKFLOW_TRIGGER_TYPE_OPTIONS = [
  {
    value: EWorkflowTriggerType.MANUAL,
    labelKey: "workflows.triggerTypes.manual",
  },
  {
    value: EWorkflowTriggerType.SCHEDULED,
    labelKey: "workflows.triggerTypes.scheduled",
  },
  {
    value: EWorkflowTriggerType.EVENT,
    labelKey: "workflows.triggerTypes.event",
  },
] as const;

export const WORKFLOW_STEP_TYPE_OPTIONS = [
  {
    value: EWorkflowStepType.AGENT_TURN,
    labelKey: "workflows.stepTypes.agent_turn",
  },
  {
    value: EWorkflowStepType.TOOL_CALL,
    labelKey: "workflows.stepTypes.tool_call",
  },
  {
    value: EWorkflowStepType.APPROVAL_GATE,
    labelKey: "workflows.stepTypes.approval_gate",
  },
] as const;

// NOTE: A workflow can also chain off another workflow's completion or failure
// (docs/automation/workflows.md, "Chaining Workflows via Events"), so it offers
// the two workflow events the event-rule dropdown keeps out of its own list.
export const WORKFLOW_EVENT_TYPE_OPTIONS = [
  ...HOOK_EVENT_TYPE_OPTIONS,
  ...NON_SELECTABLE_HOOK_EVENT_TYPE_OPTIONS,
] as const;

// NOTE: Run statuses a run can still move out of; the history polls while one
// of these is on screen.
export const ACTIVE_WORKFLOW_RUN_STATUSES: readonly string[] = [
  EWorkflowRunStatus.RUNNING,
  EWorkflowRunStatus.WAITING_APPROVAL,
];

export const WORKFLOW_RUN_STATUS_LABEL_KEYS = {
  [EWorkflowRunStatus.RUNNING]: "workflows.runStatus.running",
  [EWorkflowRunStatus.WAITING_APPROVAL]: "workflows.runStatus.waiting_approval",
  [EWorkflowRunStatus.OK]: "workflows.runStatus.ok",
  [EWorkflowRunStatus.ERROR]: "workflows.runStatus.error",
  [EWorkflowRunStatus.CANCELLED]: "workflows.runStatus.cancelled",
  [EWorkflowRunStatus.REJECTED]: "workflows.runStatus.rejected",
} as const;

export const WORKFLOW_STEP_RUN_STATUS_LABEL_KEYS = {
  [EWorkflowStepRunStatus.RUNNING]: "workflows.runStatus.running",
  [EWorkflowStepRunStatus.OK]: "workflows.runStatus.ok",
  [EWorkflowStepRunStatus.ERROR]: "workflows.runStatus.error",
  [EWorkflowStepRunStatus.SKIPPED]: "workflows.runStatus.skipped",
  [EWorkflowStepRunStatus.WAITING_APPROVAL]:
    "workflows.runStatus.waiting_approval",
  [EWorkflowStepRunStatus.APPROVED]: "workflows.runStatus.approved",
  [EWorkflowStepRunStatus.REJECTED]: "workflows.runStatus.rejected",
} as const;
